import {StreamSequenceAccumulator} from './stream-sequence-accumulator';

export interface StreamArgumentChunk {
  argumentName: string;
  content: string;
  sequenceNumber: number;
}

export interface ToolCallStreamState {
  toolCallId: string;
  toolName: string;
  arguments: Record<string, string>;
}

interface ToolCallEntry {
  toolName: string;
  arguments: Map<string, StreamSequenceAccumulator>;
}

/**
 * Accumulates tool call argument deltas per tool call.
 *
 * Each (toolCallId, argumentName) pair gets its own StreamSequenceAccumulator
 * to handle sequencing independently. After processing deltas, returns the
 * current state snapshot for the callback.
 */
export class ToolCallStreamAccumulator {
  private toolCalls: Map<string, ToolCallEntry> = new Map();

  processArgumentDeltas(
    toolCallId: string,
    toolName: string,
    deltas: StreamArgumentChunk[],
  ): ToolCallStreamState {
    let entry = this.toolCalls.get(toolCallId);
    if (!entry) {
      entry = {toolName, arguments: new Map()};
      this.toolCalls.set(toolCallId, entry);
    }

    for (const delta of deltas) {
      let accumulator = entry.arguments.get(delta.argumentName);
      if (!accumulator) {
        accumulator = new StreamSequenceAccumulator({initialSequenceNumber: 0});
        entry.arguments.set(delta.argumentName, accumulator);
      }
      accumulator.processChunk({
        content: delta.content,
        sequenceNumber: delta.sequenceNumber,
      });
    }

    const args: Record<string, string> = {};
    for (const [name, acc] of entry.arguments) {
      args[name] = acc.getContent();
    }

    return {
      toolCallId,
      toolName: entry.toolName,
      arguments: args,
    };
  }

  reset(): void {
    this.toolCalls.clear();
  }
}
